import React, { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtINR, fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Users, PlayCircle, FileDown, Trash2, ArrowRight } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const monthOptions = () => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const now = new Date();
    const out = [];
    for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        out.push(`${months[d.getMonth()]} ${d.getFullYear()}`);
    }
    return out;
};

export default function Payroll() {
    const [runs, setRuns] = useState([]);
    const [summary, setSummary] = useState({});
    const [selected, setSelected] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const refresh = async () => {
        const r = await api.get("/payroll/runs");
        setRuns(r.data.runs);
        setSummary(r.data.summary);
    };
    useEffect(() => { refresh(); }, []);

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// PAYROLL ENGINE · INDIA STANDARD</div>
                    <h1 className="font-display font-black text-3xl flex items-center gap-3"><Users className="text-[var(--brand-amber)]" size={26} /> Salaries, Deductions & Slips</h1>
                    <div className="font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider mt-1">
                        PF (12% · ₹15K ceiling) · ESIC (0.75% · ≤₹21K) · TDS (New Regime slabs) · Professional Tax (₹200) · Auto-posts JV
                    </div>
                </div>
                <button data-testid="pr-run" onClick={() => setShowForm(true)} className="px-3 py-1.5 bg-[var(--brand-amber)] text-black font-mono-fin uppercase text-[10px] tracking-[0.15em] font-semibold flex items-center gap-2 hover:bg-[var(--bg-panel-hover)]">
                    <PlayCircle size={13} /> Run Payroll
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard testId="pr-runs" label="Runs Completed" value={runs.length} format="custom" accent />
                <KpiCard testId="pr-ytd-paid" label="Total Paid (Net YTD)" value={summary.total_paid_ytd || 0} />
                <KpiCard testId="pr-ytd-tds" label="TDS Deposited YTD" value={summary.total_tds || 0} />
                <KpiCard testId="pr-ytd-pf" label="PF Deposited YTD" value={summary.total_pf || 0} />
            </div>

            <Panel testId="pr-runs-list" title="Payroll Runs" subtitle="Each run auto-generates a journal voucher — visible in Journals / Ledger">
                {runs.length === 0 ? (
                    <div className="text-center py-8 text-[var(--text-secondary)] font-mono-fin text-xs uppercase tracking-wider">No payroll runs yet. Click 'Run Payroll' to process the current month.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full font-mono-fin text-xs">
                            <thead className="border-b border-panel"><tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                <th className="text-left py-2">Run No</th><th>Period</th><th>Employees</th><th className="text-right">Gross</th><th className="text-right">Deductions</th><th className="text-right">Net</th><th>JV</th><th></th><th></th>
                            </tr></thead>
                            <tbody>
                                {runs.map((r) => (
                                    <tr key={r.id} data-testid={`pr-row-${r.id}`} className="border-b border-[var(--border)]/50 hover:bg-[var(--bg-panel-hover)]">
                                        <td className="py-2 text-[var(--brand-amber)]">{r.run_no}</td>
                                        <td className="text-[var(--text-primary)]">{r.period}</td>
                                        <td className="text-[var(--text-secondary)]">{r.employee_count}</td>
                                        <td className="text-right text-[var(--text-primary)]">{fmtINR(r.total_gross)}</td>
                                        <td className="text-right text-neg">{fmtINR((r.total_gross || 0) - (r.total_net || 0))}</td>
                                        <td className="text-right text-pos font-semibold">{fmtINR(r.total_net)}</td>
                                        <td className="text-[var(--brand-amber)]">{r.journal_voucher_no || "—"}</td>
                                        <td><button data-testid={`pr-view-${r.id}`} onClick={() => setSelected(r)} className="text-[10px] uppercase tracking-wider text-[var(--brand-amber)] hover:underline">View Slips <ArrowRight size={10} className="inline" /></button></td>
                                        <td><button data-testid={`pr-del-${r.id}`} onClick={async () => { if (confirm("Delete this run?")) { await api.delete(`/payroll/runs/${r.id}`); toast.success("Deleted"); refresh(); }}} className="text-[var(--text-muted)] hover:text-neg"><Trash2 size={12} /></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>

            {showForm && <RunForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); refresh(); }} />}
            {selected && <SlipsModal run={selected} onClose={() => setSelected(null)} />}
        </div>
    );
}

function RunForm({ onClose, onSaved }) {
    const [form, setForm] = useState({ period: monthOptions()[0], month_days: 26, bank_id: "B-01", default_days_present: 26 });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        try {
            setBusy(true);
            const res = await api.post("/payroll/runs", { ...form, month_days: Number(form.month_days), default_days_present: Number(form.default_days_present) });
            toast.success(`${res.data.run_no} posted · Net paid ₹${(res.data.total_net / 100000).toFixed(1)}L`);
            onSaved();
        } catch (e) { toast.error("Failed: " + (e.response?.data?.detail || e.message)); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-40 bg-[var(--bg-base)]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className="w-full max-w-lg bg-panel border border-[var(--brand-amber)]" onClick={(e) => e.stopPropagation()}>
                <div className="border-b border-panel px-4 py-3 flex items-center justify-between">
                    <div className="text-xs font-mono-fin uppercase tracking-[0.15em] text-[var(--brand-amber)]">Run Payroll — Current Employees</div>
                    <button onClick={onClose} className="text-[var(--text-secondary)]">✕</button>
                </div>
                <div className="p-4 space-y-3">
                    <div><label className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Period</label>
                        <select data-testid="pr-period" value={form.period} onChange={(e) => set("period", e.target.value)} className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin text-[var(--text-primary)] mt-1">
                            {monthOptions().map((m) => <option key={m}>{m}</option>)}
                        </select></div>
                    <div><label className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Month Working Days</label>
                        <input data-testid="pr-days" type="number" value={form.month_days} onChange={(e) => set("month_days", e.target.value)} className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin text-[var(--text-primary)] mt-1" /></div>
                    <div><label className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Default Days Present</label>
                        <input data-testid="pr-present" type="number" value={form.default_days_present} onChange={(e) => set("default_days_present", e.target.value)} className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin text-[var(--text-primary)] mt-1" /></div>
                    <div><label className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Payment Bank</label>
                        <select value={form.bank_id} onChange={(e) => set("bank_id", e.target.value)} className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin text-[var(--text-primary)] mt-1">
                            <option value="B-01">HDFC Current</option>
                            <option value="B-02">SBI Current</option>
                            <option value="B-05">BoB Current</option>
                        </select></div>
                    <button data-testid="pr-submit" onClick={submit} disabled={busy} className="w-full mt-2 px-4 py-2 bg-[var(--brand-amber)] text-black font-mono-fin uppercase text-xs tracking-[0.15em] font-semibold disabled:opacity-50">
                        {busy ? "Processing..." : "Post Run & Auto-JV"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function SlipsModal({ run, onClose }) {
    const download = (empId) => {
        window.open(`${API}/payroll/slip/${run.id}/${empId}`, "_blank");
    };
    return (
        <div className="fixed inset-0 z-40 bg-[var(--bg-base)]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className="w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-panel border border-[var(--brand-amber)]" onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-panel border-b border-panel px-4 py-3 flex items-center justify-between">
                    <div>
                        <div className="text-xs font-mono-fin uppercase tracking-[0.15em] text-[var(--brand-amber)]">Payroll {run.period} · {run.run_no}</div>
                        <div className="text-[10px] font-mono-fin text-[var(--text-secondary)] uppercase tracking-wider">{run.employee_count} employees · Net paid {fmtINR(run.total_net)} · JV {run.journal_voucher_no}</div>
                    </div>
                    <button onClick={onClose} data-testid="pr-slips-close" className="text-[var(--text-secondary)]">✕</button>
                </div>
                <div className="p-4 overflow-x-auto">
                    <table className="w-full font-mono-fin text-xs">
                        <thead className="border-b border-panel"><tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                            <th className="text-left py-2">ID</th><th>Name</th><th>Role</th><th className="text-right">Days</th><th className="text-right">Gross</th><th className="text-right">PF</th><th className="text-right">ESIC</th><th className="text-right">TDS</th><th className="text-right">PT</th><th className="text-right">Net</th><th></th>
                        </tr></thead>
                        <tbody>
                            {run.slips.map((s) => (
                                <tr key={s.employee_id} data-testid={`pr-slip-${s.employee_id}`} className="border-b border-[var(--border)]/50 hover:bg-[var(--bg-panel-hover)]">
                                    <td className="py-2 text-[var(--brand-amber)]">{s.employee_id}</td>
                                    <td className="text-[var(--text-primary)]">{s.employee_name}</td>
                                    <td className="text-[var(--text-secondary)]">{s.role}</td>
                                    <td className="text-right">{s.days_present}/{s.month_days}</td>
                                    <td className="text-right text-[var(--text-primary)]">{fmtINR(s.gross)}</td>
                                    <td className="text-right text-neg">{fmtINR(s.pf)}</td>
                                    <td className="text-right text-neg">{fmtINR(s.esic)}</td>
                                    <td className="text-right text-neg">{fmtINR(s.tds)}</td>
                                    <td className="text-right text-neg">{fmtINR(s.pt)}</td>
                                    <td className="text-right text-pos font-semibold">{fmtINR(s.net_pay)}</td>
                                    <td><button data-testid={`pr-pdf-${s.employee_id}`} onClick={() => download(s.employee_id)} className="text-[10px] uppercase tracking-wider text-[var(--brand-amber)] hover:underline flex items-center gap-1"><FileDown size={11} /> PDF</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
